<template>
    <div class="bg-gray-100">
        <TopLocationHero title-key="museum_of_fine_arts.title" static-image-key="museum-of-fine-arts___hero" />

        <div class="relative mt-4 sm:mt-12 z-1">
            <a id="main-content" class="fixed top-0" tabindex="-1"></a>

            <div class="container hidden sm:block">
                <Breadcrumb :items="breadcrumbItems" />
            </div>

            <TopLocationBackgroundShape1 />
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- INTRO -->
        <!-- * -->
        <div class="container relative z-2 mt-12">
            <Type6Block
                featured
                title-key="museum_of_fine_arts.intro.title"
                description-key="museum_of_fine_arts.intro.description"
                details-description-key="museum_of_fine_arts.intro.details"
                :images="galleryImages1"
            />

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- TRADITIONS -->
            <!-- * -->
            <Type9Block
                title-key="museum_of_fine_arts.traditions.title"
                description-key="museum_of_fine_arts.traditions.description"
                static-image-key="museum-of-fine-arts___traditions"
                class="mt-16 sm:mt-20"
            />

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- TIME TRAVEL INTO THE PAST -->
            <!-- * -->

            <Type8Block
                title-key="museum_of_fine_arts.time_travel.title"
                description-key="museum_of_fine_arts.time_travel.description"
                class="mt-16 sm:mt-20"
            />
        </div>

        <div class="relative overflow-hidden">
            <TopLocationBackgroundShape2
                class="top-0 sm:top-72 md:top-64 lg:top-12 left-0 -rotate-12 -translate-x-1/4"
            />

            <div class="container relative z-2">
                <div class="grid grid-cols-12 base-responsive-gap">
                    <div class="col-span-12 lg:col-span-8 lg:col-start-3">
                        <StaticImage
                            img-key="museum-of-fine-arts___time-travel"
                            class="mt-16 sm:mt-20"
                            wrapper-class="aspect-w-16 aspect-h-9 base-responsive-rounding"
                            image-class="w-full h-full object-cover"
                        />
                    </div>
                </div>

                <div class="grid grid-cols-12 gap-x-7 gap-y-5 md:gap-y-12 mt-16 sm:mt-20">
                    <div class="col-span-12 md:col-span-6 xl:col-span-5 xl:col-start-3 order-1">
                        <T t="museum_of_fine_arts.time_travel.content_1" />
                    </div>
                    <div class="col-span-12 md:col-span-6 xl:col-span-4 order-2">
                        <StaticImage
                            img-key="museum-of-fine-arts___time-travel-content-1"
                            max-size="sm"
                            image-class="w-full h-full object-cover"
                            wrapper-class="base-responsive-rounding"
                        />
                    </div>

                    <div class="col-span-12 md:col-span-6 xl:col-start-2 order-4 md:order-3">
                        <StaticImage
                            img-key="museum-of-fine-arts___time-travel-content-2"
                            max-size="sm"
                            image-class="w-full h-full object-cover"
                            wrapper-class="base-responsive-rounding aspect-w-16 aspect-h-9"
                        />
                    </div>
                    <div class="col-span-12 md:col-span-6 xl:col-span-3 order-3 md:order-4">
                        <T t="museum_of_fine_arts.time_travel.content_2" />
                    </div>

                    <div class="col-span-12 md:col-span-6 xl:col-span-5 xl:col-start-3 order-5">
                        <T t="museum_of_fine_arts.time_travel.content_3" />
                    </div>
                    <div class="col-span-12 md:col-span-6 xl:col-span-4 order-6">
                        <StaticImage
                            img-key="museum-of-fine-arts___time-travel-3"
                            max-size="sm"
                            image-class="w-full h-full object-cover"
                            wrapper-class="base-responsive-rounding"
                        />
                    </div>
                </div>
            </div>
        </div>

        <div class="container">
            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- DID YOU KNOW? -->
            <!-- * -->
            <Type8Block
                title-key="museum_of_fine_arts.did_you_know.title"
                description-key="museum_of_fine_arts.did_you_know.description"
                class="mt-16 sm:mt-20"
            />

            <!-- * -->
            <!-- * -->
            <!-- * -->
            <!-- ROMAN HALL -->
            <!-- * -->
            <Type8Block
                title-key="museum_of_fine_arts.roman_hall.title"
                description-key="museum_of_fine_arts.roman_hall.description"
                class="mt-16 sm:mt-20"
            />

            <div class="grid grid-cols-12 base-responsive-gap">
                <div class="col-span-12 lg:col-span-10 lg:col-start-2">
                    <div class="aspect-w-16 aspect-h-9 mt-8 sm:mt-16 lg:mt-20">
                        <iframe
                            src="https://www.youtube.com/embed/xWegW1EO2WE?si=h1XLKD-UZJUcRoLH"
                            title="YouTube video player"
                            frameborder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                            referrerpolicy="strict-origin-when-cross-origin"
                            allowfullscreen
                        ></iframe>
                    </div>
                </div>
            </div>
        </div>
        <div class="relative z-2 pt-12 lg:pt-0">
            <TopLocationBackgroundShape3 class="top-12 xl:top-20" />

            <div class="container">
                <Type11Block
                    :slides="[
                        {
                            titleKey: 'museum_of_fine_arts.features_1.title',
                            descriptionKey: 'museum_of_fine_arts.features_1.description',
                            imageKey: 'museum-of-fine-arts___features-slide-1',
                        },
                        {
                            titleKey: 'museum_of_fine_arts.features_2.title',
                            descriptionKey: 'museum_of_fine_arts.features_2.description',
                            imageKey: 'museum-of-fine-arts___features-slide-2',
                        },
                        {
                            titleKey: 'museum_of_fine_arts.features_3.title',
                            descriptionKey: 'museum_of_fine_arts.features_3.description',
                            imageKey: 'museum-of-fine-arts___features-slide-3',
                        },
                        {
                            titleKey: 'museum_of_fine_arts.features_4.title',
                            descriptionKey: 'museum_of_fine_arts.features_4.description',
                            imageKey: 'museum-of-fine-arts___features-slide-4',
                        },
                        {
                            titleKey: 'museum_of_fine_arts.features_5.title',
                            descriptionKey: 'museum_of_fine_arts.features_5.description',
                            imageKey: 'museum-of-fine-arts___features-slide-5',
                        },
                        {
                            titleKey: 'museum_of_fine_arts.features_6.title',
                            descriptionKey: 'museum_of_fine_arts.features_6.description',
                            imageKey: 'museum-of-fine-arts___features-slide-6',
                        },
                    ]"
                />

                <!-- * -->
                <!-- * -->
                <!-- * -->
                <!-- COLLECTIONS -->
                <!-- * -->
                <Type4Block
                    title-key="museum_of_fine_arts.collections.title"
                    description-key="museum_of_fine_arts.collections.description"
                    :images="galleryImages2"
                    class="mt-16 sm:mt-20"
                />
            </div>
        </div>
        <div class="relative z-1">
            <TopLocationBackgroundShape4 class="-top-32 lg:-top-36 xl:-top-44 3xl:-top-40" />

            <div class="container relative z-1">
                <!-- * -->
                <!-- * -->
                <!-- * -->
                <!-- iWALL -->
                <!-- * -->
                <Type9Block
                    title-key="museum_of_fine_arts.iwall.title"
                    description-key="museum_of_fine_arts.iwall.description"
                    static-image-key="museum-of-fine-arts___iwall"
                    class="mt-16 sm:mt-20"
                />

                <!-- * -->
                <!-- * -->
                <!-- * -->
                <!-- ADVICE -->
                <!-- * -->
                <Type8Block
                    title-key="museum_of_fine_arts.advice.title"
                    description-key="museum_of_fine_arts.advice.description"
                    class="mt-16 sm:mt-20"
                />
            </div>
        </div>

        <!-- * -->
        <!-- * -->
        <!-- * -->
        <!-- GALLERY -->
        <!-- * -->
        <XXLGalleryCarousel v-if="galleryImages3" :images="galleryImages3" class="mt-16 sm:mt-20" />

        <div class="container mt-16 sm:mt-20">
            <TopLocationProjectPageBanner
                v-if="$t('museum_of_fine_arts.event_recommendation_button_url').length"
                :button-text="$t('museum_of_fine_arts.event_recommendation_button_label')"
                :button-url="$t('museum_of_fine_arts.event_recommendation_button_url')"
                label-text-key="museum_of_fine_arts.event_recommendation_text"
                type="event"
            />

            <TopLocationProjectPageBanner
                :button-text="$t('museum_of_fine_arts.visit_project_button_label')"
                :button-url="$t('museum_of_fine_arts.visit_project_button_url')"
                label-text-key="museum_of_fine_arts.visit_project_text"
                class="mt-4 sm:mt-8"
            />

            <AwardsBlock
                v-if="awards"
                :title="awards.title"
                :description="awards.description"
                :media="awards.media"
                class="mt-16 sm:mt-20"
            />
        </div>
    </div>
</template>

<script>
import Type9Block from '@/components/Blocks/Type9Block/Type9Block.vue';
import Type11Block from '@/components/Blocks/Type11Block/Type11Block.vue';
import TopLocationProjectPageBanner from '@/components/Pages/TopLocation/TopLocationProjectPageBanner.vue';
import AwardsBlock from '@/components/Blocks/AwardsBlock/AwardsBlock.vue';
import TopLocationHero from '@/components/Pages/TopLocation/Hero/Hero.vue';
import TopLocationBackgroundShape1 from '@/components/Pages/TopLocation/TopLocationBackgroundShape1.vue';
import Type8Block from '@/components/Blocks/Type8Block/Type8Block.vue';
import Type6Block from '@/components/Blocks/Type6Block/Type6Block.vue';
import Type4Block from '@/components/Blocks/Type4Block/Type4Block.vue';
import XXLGalleryCarousel from '@/components/Carousels/XXLGalleryCarousel/XXLGalleryCarousel.vue';
import TopLocationBackgroundShape2 from '@/components/Pages/TopLocation/TopLocationBackgroundShape2.vue';
import TopLocationBackgroundShape3 from '@/components/Pages/TopLocation/TopLocationBackgroundShape3.vue';
import TopLocationBackgroundShape4 from '@/components/Pages/TopLocation/TopLocationBackgroundShape4.vue';

export default {
    name: 'MuseumOfFineArts',
    components: {
        TopLocationBackgroundShape4,
        TopLocationBackgroundShape3,
        TopLocationBackgroundShape2,
        XXLGalleryCarousel,
        Type4Block,
        Type11Block,
        Type9Block,
        Type6Block,
        AwardsBlock,
        TopLocationProjectPageBanner,
        Type8Block,
        TopLocationBackgroundShape1,
        TopLocationHero,
    },
    async asyncData({ $axios, i18n }) {
        let galleryImages1 = null;
        let galleryImages2 = null;
        let galleryImages3 = null;
        let awards = null;

        await Promise.allSettled([
            $axios.$get('/galleries/reference/top-location___museum-of-fine-arts-1'),
            $axios.$get('/galleries/reference/top-location___museum-of-fine-arts-2'),
            $axios.$get('/galleries/reference/top-location___museum-of-fine-arts-3'),
            $axios.$get('/awards/reference/museum-of-fine-arts'),
        ])
            .then(([galleryImages1Res, galleryImages2Res, galleryImages3Res, awardsRes]) => {
                if (galleryImages1Res.status === 'fulfilled') {
                    galleryImages1 = galleryImages1Res.value.slides;
                }

                if (galleryImages2Res.status === 'fulfilled') {
                    galleryImages2 = galleryImages2Res.value.slides;
                }

                if (galleryImages3Res.status === 'fulfilled') {
                    galleryImages3 = galleryImages3Res.value.slides;
                }

                if (awardsRes.status === 'fulfilled' && awardsRes.value.is_on_top_locations) {
                    awards = awardsRes.value;
                }
            })
            .catch((err) => {
                console.log(err);
            });

        const breadcrumbItems = [];

        breadcrumbItems.push([i18n.t('navigation.top_locations'), 'top-locations', true]);
        breadcrumbItems.push([i18n.t('museum_of_fine_arts.title'), 'museum-of-fine-arts']);

        return {
            galleryImages1,
            galleryImages2,
            galleryImages3,
            awards,
            breadcrumbItems,
        };
    },
    data() {
        return {};
    },
    head() {
        return {
            title: this.$getPageTitle('top-locations.museum-of-fine-arts'),
            meta: this.$getPageMetaData('top-locations.museum-of-fine-arts'),
        };
    },
};
</script>

<style scoped></style>
